import apiClient from '../config/axios';
import { getActiveSystem } from '../config/system';

// Cross-system link resilience for detail pages (GitHub issue #92).
//
// Content is hard-scoped by system, so a detail fetch (GET /api/<entity>/<slug>)
// 404s when the slug isn't in the active system. The bulk of existing shared /
// bookmarked / indexed links — and the legacy links baked into imported 3.5e
// content — carry no ?system= param, so under the pf-1e default they land on the
// wrong system and break. When that happens we ask the backend which enabled
// system actually owns the slug and, if it's a *different* system, redirect the
// link there (self-healing the URL with ?system= on the way).

// The entity path segment (e.g. 'spells') for a detail page. Used to build the
// resolver URL and to preserve the current path on redirect.
function currentEntityPath() {
  // window.location.pathname is '/spells', '/feats', … for every detail route.
  return window.location.pathname.replace(/^\/+|\/+$/g, '');
}

// On a detail 404, try to recover the link by pointing it at the system that
// owns the slug. Returns true if a redirect was initiated (caller should stay in
// its loading state rather than flash the error), false otherwise.
//
// The "different from the active system" guard is what prevents a redirect loop:
// after we redirect, the owning system becomes active, the resolver returns that
// same system, and with no *other* system to move to we stop and let the page
// show its normal not-found error.
export async function attemptCrossSystemRedirect(entity, slug) {
  if (!entity || !slug) return false;
  const active = getActiveSystem();
  try {
    const { data } = await apiClient.get(
      `/api/resolve/${encodeURIComponent(entity)}/${encodeURIComponent(slug)}`,
    );
    const owner = (data?.systems || []).find((s) => s && s !== active);
    if (!owner) return false;

    const url = new URL(window.location.href);
    url.searchParams.set('slug', slug);
    url.searchParams.set('system', owner);
    // Full-load navigation so config/system.js re-reads ?system= at module load
    // and every subsequent fetch is scoped to the owning system.
    window.location.assign(url.pathname + url.search);
    return true;
  } catch (e) {
    return false;
  }
}

// After a detail view resolves successfully, stamp the active system into the
// address bar if it isn't already there, so any URL the user copies from here on
// carries ?system= and no longer depends on the viewer's default (issue #92,
// "more resilient moving forward"). replaceState keeps it out of history and
// triggers no reload.
export function ensureSystemInUrl() {
  try {
    const url = new URL(window.location.href);
    // Only stamp addresses that resolve content: a detail view (?slug=) or a
    // compare view (?compare=, issue 188) — both are shared as deep links and
    // must be self-describing (#92's rule C).
    if (!url.searchParams.get('slug') && !url.searchParams.get('compare')) return;
    if (url.searchParams.get('system')) return;
    url.searchParams.set('system', getActiveSystem());
    window.history.replaceState(window.history.state, '', url.pathname + url.search);
  } catch (e) {
    /* no-op: history/URL unavailable */
  }
}

export { currentEntityPath };
